import { ClassificationLabels } from "../../../models/classification.map";
import { PENDING_MEDICAMENT_DESTINATION } from "./commandeConstants";

// Brouillon d'une commande : conversion depuis et vers le serveur, clés locales et aperçu dans la liste.

let nextDraftKey = 1;

export function createDraftKey(prefix) {
    return `${prefix}-${Date.now()}-${nextDraftKey++}`;
}

export function toDateInputValue(dateString) {
    if (!dateString) return "";
    return dateString.split("T")[0];
}

export function toTimeInputValue(dateTimeString) {
    if (!dateTimeString) return "";
    const d = new Date(dateTimeString);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function buildDateTime(date, time) {
    if (!time) return null;
    const d = date || new Date().toISOString().split("T")[0];
    return `${d}T${time}:00`;
}

export function isUnresolvedMedicamentDestination(destination) {
    return !destination || destination === PENDING_MEDICAMENT_DESTINATION;
}

export function resolveMedicamentDestination(destination, defaultDestination = PENDING_MEDICAMENT_DESTINATION) {
    if (destination === PENDING_MEDICAMENT_DESTINATION) return PENDING_MEDICAMENT_DESTINATION;
    return destination || defaultDestination || PENDING_MEDICAMENT_DESTINATION;
}

// Valeur de chaque champ, ou sa valeur par défaut quand il est absent (null ou undefined).
function withDefaults(source, defaults) {
    return Object.fromEntries(Object.entries(defaults).map(([field, fallback]) => [field, source[field] ?? fallback]));
}

const PRODUIT_DEFAULTS = {
    id: null,
    nom: "",
    codeCis: "",
    cip13: "",
    bdpmDisponibiliteStatutCodes: "",
    bdpmDisponibiliteStatuts: "",
    dosage: "",
    posologie: "",
    quantite: 1,
    statut: null,
    siBesoin: false,
    classificationMedicament: null,
};
const PRODUIT_FOLLOW_UP_DEFAULTS = {
    billingValidated: false,
    billingUsername: null,
    planificationValidated: null,
    planificationValidatedAt: null,
    planificationValidatedBy: null,
    commentaryPlanificationRobot: null,
    commentaryPlanificationEtiquetage: null,
    ehpadWarnedIfOutOfStock: false,
    pharmaceuticalValidated: false,
    managementValidated: true,
    managementValidatedAt: null,
    managementValidatedBy: null,
    pharmaceuticalValidatedAt: null,
    pharmaceuticalValidatedBy: null,
};

function produitToBackend(prod, defaultDestination) {
    return {
        clientKey: prod._clientKey ?? null,
        ...withDefaults(prod, PRODUIT_DEFAULTS),
        destination: resolveMedicamentDestination(prod.destination, defaultDestination),
        deliveryDate: prod.deliveryDate ?? null,
        billingHour: buildDateTime(prod.deliveryDate, prod.billingHourTime),
        ...withDefaults(prod, PRODUIT_FOLLOW_UP_DEFAULTS),
    };
}

function patientIdentityResolution(p) {
    return p._identityResolution ?? (p._createdInCurrentDraft && p.id != null ? "UPDATE_EXISTING" : null);
}

function patientToBackend(p, defaultDestination) {
    return {
        commandePatientId: p.commandePatientId ?? null,
        id: p.id ?? null,
        nom: p.nom,
        prenom: p.prenom,
        dateNaissance: p.dateNaissance,
        classification: p.classification || "UNKNOWN",
        ...withDefaults(p, {
            commentaryProductionRobot: null,
            commentaryProductionEtiquetage: null,
            pharmaceuticalValidated: false,
            managementValidated: true,
            managementValidatedAt: null,
            managementValidatedBy: null,
            pharmaceuticalValidatedAt: null,
            pharmaceuticalValidatedBy: null,
        }),
        identityResolution: patientIdentityResolution(p),
        identityCandidateId: p._identityCandidateId ?? null,
        produits: (p.produits || []).map(prod => produitToBackend(prod, defaultDestination)),
    };
}

export function transformToBackend(draft, defaultDestination = "ROBOT") {
    if (!draft) return null;

    return {
        id: draft.id,
        ehpad: draft.ehpadId ? { id: draft.ehpadId } : null,
        statut: draft.statut,
        destinationCommande: draft.destination,
        source: draft.source,
        serviceKey: draft.serviceKey,
        commentaire: draft.commentaire,
        emailEmetteur: draft.emailEmetteur,
        patients: (draft.patients || []).map(p => patientToBackend(p, defaultDestination)),
    };
}

function produitFromBackend(m) {
    return {
        _clientKey: m.clientKey ?? m._clientKey ?? createDraftKey("medicament"),
        ...withDefaults(m, PRODUIT_DEFAULTS),
        destination: m.destination ?? PENDING_MEDICAMENT_DESTINATION,
        deliveryDate: toDateInputValue(m.deliveryDate),
        billingHourTime: toTimeInputValue(m.billingHour),
        ...withDefaults(m, PRODUIT_FOLLOW_UP_DEFAULTS),
    };
}

function patientFromBackend(p) {
    return {
        _clientKey: p._clientKey ?? createDraftKey("patient"),
        _createdInCurrentDraft: p._createdInCurrentDraft === true,
        commandePatientId: p.commandePatientId ?? p.id,
        ...withDefaults(p, {
            id: null,
            nom: "",
            prenom: "",
            dateNaissance: null,
            classification: null,
            commentaryProductionRobot: "",
            commentaryProductionEtiquetage: "",
            pharmaceuticalValidated: false,
            managementValidated: true,
            managementValidatedAt: null,
            managementValidatedBy: null,
            pharmaceuticalValidatedAt: null,
            pharmaceuticalValidatedBy: null,
        }),
        produits: (p.produits || []).map(produitFromBackend),
    };
}

function workflowFromBackend(data) {
    return {
        managementStatut: data.managementStatut ?? "VALIDE",
        managementValidatedAt: data.managementValidatedAt ?? null,
        managementValidatedBy: data.managementValidatedBy ?? null,
        pharmaceuticalValidatedAt: data.pharmaceuticalValidatedAt ?? null,
        pharmaceuticalValidatedBy: data.pharmaceuticalValidatedBy ?? null,
    };
}

function filesFromBackend(data) {
    return (data.fileNames || []).map((name, index) => ({
        name,
        path: data.filePaths?.[index] ?? null,
        id: data.ordonnanceIds?.[index] ?? null,
    }));
}

export function fromBackendToDraft(data) {
    if (!data) return null;

    return {
        id: data.id,
        ehpadId: data.ehpad?.id ?? null,
        statut: data.statut ?? null,
        ...workflowFromBackend(data),
        destination: data.destinationCommande,
        source: data.source ?? null,
        serviceKey: data.serviceKey ?? "GENERAL",
        commentaire: data.commentaire ?? "",
        emailEmetteur: data.emailEmetteur ?? "",
        files: filesFromBackend(data),
        patients: (data.patients || []).map(patientFromBackend),
        messages: data.messages ?? [],
        favorite: data.favorite ?? false
    };
}

export function ensureDraftClientKeys(draft) {
    if (!draft) return draft;
    return {
        ...draft,
        patients: (draft.patients || []).map(patient => ({
            ...patient,
            _clientKey: patient._clientKey ?? createDraftKey("patient"),
            produits: (patient.produits || []).map(produit => ({
                ...produit,
                _clientKey: produit._clientKey ?? createDraftKey("medicament"),
            })),
        })),
    };
}

export function cloneDraft(draft) {
    return draft ? JSON.parse(JSON.stringify(draft)) : draft;
}

export function formatMedicamentName(prod) {
    const nom = prod?.nom ?? "";
    const dosage = prod?.dosage ?? "";
    return `${nom}${dosage ? ` ${dosage}` : ""}`.trim();
}

export function draftToListPatch(draft, defaultDestination = "ROBOT") {
    if (!draft?.id) return null;

    const patients = (draft.patients || []).map(p => ({
        commandePatientId: p.commandePatientId ?? null,
        prenom: p.prenom ?? "",
        nom: p.nom ?? "",
        classification: ClassificationLabels[p.classification] ?? p.classification ?? null,
        pharmaceuticalValidated: p.pharmaceuticalValidated ?? false,
        medicaments: (p.produits || [])
            .map(prod => ({
                id: prod.id ?? null,
                nom: formatMedicamentName(prod),
                destination: resolveMedicamentDestination(prod.destination, defaultDestination),
                statut: prod.statut ?? null,
                billingValidated: prod.billingValidated ?? false,
                billingHour: prod.billingHour ?? null,
                billingUsername: prod.billingUsername ?? null,
                planificationValidated: prod.planificationValidated ?? null,
                planificationValidatedAt: prod.planificationValidatedAt ?? null,
                planificationValidatedBy: prod.planificationValidatedBy ?? null,
            }))
            .sort((a, b) => (a.destination === "ROBOT" ? 0 : 1) - (b.destination === "ROBOT" ? 0 : 1)),
    }));

    const medicaments = patients.flatMap(p => p.medicaments || []);

    return {
        statut: draft.statut,
        destination: draft.destination,
        commentaire: draft.commentaire,
        emailEmetteur: draft.emailEmetteur,
        favorite: draft.favorite ?? false,
        totalMedicaments: medicaments.length,
        medicamentsTraites: medicaments.filter(m => m.statut === "TRAITE").length,
        medicamentsLivres: medicaments.filter(m => m.statut === "LIVRE").length,
        patients,
    };
}

export function buildCommandeListPatch(data) {
    if (!data) return {};
    const patch = {};
    if (data.statut !== undefined) patch.statut = data.statut;
    if (data.managementStatut !== undefined) patch.managementStatut = data.managementStatut;
    if (data.destinationCommande !== undefined) patch.destination = data.destinationCommande;
    else if (data.destination !== undefined) patch.destination = data.destination;
    if (data.favorite !== undefined) patch.favorite = data.favorite;
    return patch;
}

export function adaptCommandeStatusToContext(commande, context) {
    const statusField = context?.statusField;
    if (!commande || !statusField || commande[statusField] == null) return commande;
    return {
        ...commande,
        pharmaceuticalStatut: commande.pharmaceuticalStatut ?? commande.statut,
        statut: commande[statusField],
    };
}

// Les listes de workflow masquent les patients non encore libérés, sans jamais filtrer le brouillon édité.
export function keepReleasedForContext(commande, context) {
    const field = context?.releasedField;
    if (!commande || !field || !Array.isArray(commande.patients)) return commande;
    const patients = commande.patients.flatMap(patient => {
        const productField = Array.isArray(patient.medicaments) ? "medicaments" : "produits";
        const products = Array.isArray(patient[productField]) ? patient[productField] : [];
        const releasedProducts = products.filter(product => product[field] !== false);
        // Une libération produit par produit rend le patient visible, même si sa
        // validation globale reste à false tant que tous ses produits ne le sont pas.
        const visible = patient[field] !== false || releasedProducts.length > 0;
        return visible ? [{ ...patient, [productField]: releasedProducts }] : [];
    });
    return { ...commande, patients };
}

export function buildCommandeDetailOptimisticPatch(patch) {
    if (!patch) return {};
    const detailPatch = {};
    if (patch.favorite !== undefined) detailPatch.favorite = patch.favorite;
    if (patch.read !== undefined) detailPatch.read = patch.read;
    return detailPatch;
}
